// Fenêtre récapitulative d'un objet du calendrier : on consulte sans quitter
// la page, puis on choisit d'ouvrir la fiche ou d'aller dans le dossier.
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Button } from '../ui/Button';
import type { CalendarEvent } from '../../services/calendar/calendarAggregate';

const KIND: Record<CalendarEvent['kind'], { label: string; dot: string; open: string | null }> = {
  hearing: { label: 'Audience', dot: 'bg-[#ea7549]', open: null },
  appointment: { label: 'Rendez-vous', dot: 'bg-cime-gold', open: 'Modifier' },
  task: { label: 'Tâche', dot: 'bg-cime-primary', open: 'Ouvrir la tâche' },
  prescription: { label: 'Prescription', dot: 'bg-red-600', open: null },
};

interface Props {
  event: CalendarEvent | null;
  /** Noms des intervenants par identifiant. */
  lawyerNames: Record<string, string>;
  onClose: () => void;
  /** Ouvre la fiche de l'objet (tâche, rendez-vous). */
  onOpen: (event: CalendarEvent) => void;
  /** Va à l'objet dans son dossier. */
  onGoToDossier: (event: CalendarEvent) => void;
}

export function CalendarEventRecap({ event, lawyerNames, onClose, onOpen, onGoToDossier }: Props) {
  if (!event) return null;
  const kind = KIND[event.kind];
  const start = new Date(event.start);
  const timed = !event.allDay && event.start.includes('T');
  const when = timed
    ? `${format(start, 'EEEE d MMMM yyyy', { locale: fr })} · ${format(start, 'HH:mm')}${
        event.end ? ` – ${format(new Date(event.end), 'HH:mm')}` : ''
      }`
    : format(start, 'EEEE d MMMM yyyy', { locale: fr });
  const people = [...new Set([event.lawyerId, ...(event.participantIds ?? [])])]
    .filter((id): id is string => !!id)
    .map((id) => lawyerNames[id])
    .filter(Boolean);

  return (
    <Dialog open onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent className="sm:max-w-md rounded-cime border-neutral-200 bg-white">
        <DialogHeader>
          <DialogDescription asChild>
            <span className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.08em] text-cime-text-muted">
              <i aria-hidden className={`h-2 w-2 rounded-full ${kind.dot}`} />
              {kind.label}
            </span>
          </DialogDescription>
          <DialogTitle
            className="text-[19px] font-semibold leading-snug text-cime-text-primary"
            style={{ fontFamily: 'var(--cime-font-heading)' }}
          >
            {event.description || event.title}
          </DialogTitle>
        </DialogHeader>

        <dl className="flex flex-col gap-2 text-[13px]">
          <Line label="Date">
            <span className="first-letter:uppercase">{when}</span>
          </Line>
          <Line label="Dossier">
            {event.dossierTitle ?? (
              <span className="text-cime-text-muted">Hors dossier · visible uniquement dans le calendrier</span>
            )}
          </Line>
          {people.length > 0 ? <Line label="Intervenants">{people.join(', ')}</Line> : null}
        </dl>

        <DialogFooter className="gap-2 sm:justify-between">
          <span>
            {kind.open ? (
              <Button type="button" variant="secondary" size="sm" onClick={() => onOpen(event)}>
                {kind.open}
              </Button>
            ) : null}
          </span>
          {event.dossierId ? (
            <Button type="button" variant="primary" size="sm" onClick={() => onGoToDossier(event)}>
              Voir dans le dossier
            </Button>
          ) : (
            <Button type="button" variant="ghost" size="sm" onClick={onClose}>
              Fermer
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Line({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex gap-3">
      <dt className="w-24 flex-shrink-0 text-neutral-400">{label}</dt>
      <dd className="m-0 min-w-0 flex-1 text-cime-text-primary">{children}</dd>
    </div>
  );
}
